import { useState, useEffect } from "react";
import { X, Crown, CheckCircle2, Loader2, Coins, Sparkles, AlertCircle, Zap, Shield, ArrowRight } from "lucide-react";
import api from "../../lib/api";

const DEFAULT_SERVICES = [
  {
    id: "vip_bronze",
    name: "VIP BRONZE",
    cost_coins: 500,
    duration_days: 30,
    color: "#cd7f32",
    perks: [
      "Tag [VIP] rezervat pe toate serverele",
      "Slot rezervat la conectare",
      "Stil de nume colorat pe forum",
      "+500 Puncte de activitate lunare"
    ]
  },
  {
    id: "vip_gold",
    name: "VIP GOLD",
    cost_coins: 1200,
    duration_days: 90,
    color: "#f59e0b",
    popular: true,
    perks: [
      "Toate avantajele VIP Bronze",
      "Skin-uri exclusive de arme în Loadout",
      "Cadru avatar animat Neon Gold",
      "Multiplicator x2 la reputație",
      "Acces în canalul privat Staff/VIP"
    ]
  },
  {
    id: "donator_diamond",
    name: "DONATOR DIAMOND",
    subtitle: "PERMANENT",
    cost_coins: 2800,
    duration_days: 3650,
    color: "#00e5ff",
    perks: [
      "Toate beneficiile VIP Gold Permanent",
      "Rol & Culoare unică pe Discord și Forum",
      "Imunitate pe serverele oficiale",
      "Toate cadrele și armele din magazin",
      "Insignă Donator Fondator pe profil"
    ]
  }
];

export default function VipPurchaseModal({ userCoins = 0, userVipTier = "none", onClose, onSuccess }) {
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    api.get("/wallet/services").then(r => {
      if (r.data?.length > 0) setServices(r.data);
    }).catch(() => {});
  }, []);

  const handlePurchase = async () => {
    if (!selected) return;
    setError("");
    setLoading(true);
    try {
      const r = await api.post("/wallet/purchase-service", { service_id: selected.id });
      if (r.data?.ok) {
        setSuccess(r.data);
        if (onSuccess) onSuccess(r.data);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Eroare la activarea serviciului.");
    } finally {
      setLoading(false);
    }
  };

  const durationLabel = (days) => {
    if (days >= 3000) return "PERMANENT";
    if (days >= 90) return `${Math.round(days / 30)} luni`;
    return `${days} zile`;
  };

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto bg-black/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl mx-auto my-8 bg-[#0d0f16] border border-white/15 rounded-xl shadow-[0_30px_80px_rgba(0,0,0,0.9)] overflow-hidden animate-scaleUp flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-white/[0.02]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <Crown className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <h2 className="font-chakra font-black text-sm uppercase tracking-widest text-white">Cumpără VIP cu IDG Coins</h2>
              <div className="flex items-center gap-1.5 mt-0.5">
                <Coins className="w-3 h-3 text-amber-400" />
                <span className="text-[11px] font-mono text-amber-400 font-bold">{userCoins.toLocaleString()} IDG disponibil</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded bg-white/5 hover:bg-rose-500/20 text-zinc-400 hover:text-rose-400 flex items-center justify-center transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 rounded bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {/* Success */}
          {success ? (
            <div className="flex flex-col items-center justify-center py-10 gap-5 text-center">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-amber-500/10 border-2 border-amber-500/40 flex items-center justify-center">
                  <Crown className="w-10 h-10 text-amber-400" />
                </div>
                <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center animate-bounce">
                  <CheckCircle2 className="w-4 h-4 text-black" />
                </div>
              </div>
              <div>
                <h3 className="font-chakra font-black text-xl text-white uppercase">VIP Activat!</h3>
                <p className="text-sm text-zinc-400 font-mono mt-1">{success.service_name} este acum activ pe contul tău.</p>
                <p className="text-xs text-zinc-500 font-mono mt-1">Activ până la: {success.vip_until?.slice(0, 10)}</p>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <Sparkles className="w-5 h-5 text-emerald-400" />
                <div className="text-left">
                  <div className="text-xs text-zinc-400 font-mono">Balanță rămasă</div>
                  <div className="font-mono font-black text-emerald-400 text-lg">{success.remaining_balance?.toLocaleString()} IDG</div>
                </div>
              </div>
              <button onClick={onClose}
                className="px-8 py-2.5 rounded-lg font-chakra font-black text-sm uppercase tracking-wider text-black"
                style={{ background: "var(--tf-primary)" }}>
                Excelent!
              </button>
            </div>
          ) : (
            <>
              {/* Service Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                {services.map(svc => {
                  const canAfford = userCoins >= svc.cost_coins;
                  const isSelected = selected?.id === svc.id;
                  const isCurrent = svc.id.includes(userVipTier) && userVipTier !== "none";

                  return (
                    <button
                      key={svc.id}
                      onClick={() => canAfford && setSelected(svc)}
                      disabled={!canAfford || isCurrent}
                      className={`relative p-4 rounded-lg border text-left transition-all duration-200 ${
                        isSelected
                          ? "scale-[1.02]"
                          : canAfford && !isCurrent
                          ? "hover:scale-[1.01] hover:border-white/25"
                          : "opacity-50 cursor-not-allowed"
                      }`}
                      style={{
                        background: isSelected ? `${svc.color}15` : "rgba(255,255,255,0.02)",
                        borderColor: isSelected ? svc.color : "rgba(255,255,255,0.1)"
                      }}
                    >
                      {svc.popular && (
                        <div className="absolute -top-2 right-3 px-2 py-0.5 text-[9px] font-black font-chakra uppercase tracking-wider rounded-full bg-amber-500 text-black">
                          POPULAR
                        </div>
                      )}
                      {isCurrent && (
                        <div className="absolute -top-2 left-3 px-2 py-0.5 text-[9px] font-black font-chakra uppercase tracking-wider rounded-full bg-emerald-500/80 text-black">
                          ACTIV
                        </div>
                      )}

                      <div className="font-chakra font-black text-white text-sm uppercase">{svc.name}</div>
                      {svc.subtitle && <div className="text-[9px] font-mono font-bold mt-0.5" style={{ color: svc.color }}>{svc.subtitle}</div>}

                      <div className="flex items-baseline gap-1 mt-2">
                        <Coins className="w-3 h-3" style={{ color: svc.color }} />
                        <span className="font-mono text-lg font-black" style={{ color: svc.color }}>
                          {svc.cost_coins?.toLocaleString()}
                        </span>
                        <span className="text-[10px] text-zinc-500 font-mono">IDG</span>
                      </div>

                      <div className="text-[10px] font-mono mt-1 mb-3" style={{ color: svc.color }}>
                        {durationLabel(svc.duration_days)}
                      </div>

                      <div className="space-y-1.5 text-left">
                        {svc.perks?.slice(0, 3).map((perk, i) => (
                          <div key={i} className="flex items-start gap-1.5">
                            <CheckCircle2 className="w-2.5 h-2.5 mt-0.5 shrink-0" style={{ color: svc.color }} />
                            <span className="text-[10px] text-zinc-400 font-mono leading-tight">{perk}</span>
                          </div>
                        ))}
                        {svc.perks?.length > 3 && (
                          <div className="text-[10px] text-zinc-500 font-mono pl-4">
                            +{svc.perks.length - 3} mai multe...
                          </div>
                        )}
                      </div>

                      {!canAfford && (
                        <div className="mt-2 text-[9px] font-mono text-rose-400">
                          Lipsesc {(svc.cost_coins - userCoins).toLocaleString()} IDG
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Purchase Panel */}
              {selected && (
                <div className="p-4 rounded-lg border space-y-3" style={{ borderColor: `${selected.color}40`, background: `${selected.color}08` }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-chakra font-black uppercase text-white">{selected.name}</div>
                      <div className="text-[10px] font-mono text-zinc-400">{durationLabel(selected.duration_days)} · {selected.cost_coins.toLocaleString()} IDG Coins</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-zinc-500" />
                    <div className="text-right">
                      <div className="text-xs font-mono text-zinc-400">Balanță după</div>
                      <div className="font-mono font-black text-sm" style={{ color: selected.color }}>
                        {(userCoins - selected.cost_coins).toLocaleString()} IDG
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-zinc-500 font-mono">
                    <Shield className="w-3 h-3 text-emerald-400" />
                    Activare instantanee · Fără comisioane suplimentare · Reversibil numai de Admin
                  </div>
                  <button
                    onClick={handlePurchase}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-chakra font-black text-sm uppercase tracking-wider text-black transition-all hover:brightness-110 disabled:opacity-50"
                    style={{ background: "var(--tf-primary)" }}
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                    {loading ? "Se activează..." : `Activează ${selected.name} — ${selected.cost_coins.toLocaleString()} IDG`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
